import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { auth, db } from "../firebase";
import { sendPasswordResetEmail, signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import { ref, get } from "firebase/database";
import { Mail, Lock, Eye, EyeOff, AlertCircle, Phone, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, loginWithGoogle, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  // Phone OTP state
  const [phoneMode, setPhoneMode] = useState(false);
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  // Set up invisible reCAPTCHA when phone mode opens
  useEffect(() => {
    if (!phoneMode) return;
    if (recaptchaRef.current) return; // already set up
    try {
      recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
      });
    } catch (e) { /* silent */ }
    return () => {
      try { recaptchaRef.current?.clear(); } catch (e) {}
      recaptchaRef.current = null;
    };
  }, [phoneMode]);

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError("Please fill in all fields."); return; }
    try {
      setError(""); setLoading(true);
      const result = await login(email, password);
      const uid = result.user.uid;
      const snap = await get(ref(db, `users/${uid}`));
      if (snap.exists() && snap.val()?.name) {
        navigate("/");
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      if (err.code === "auth/email-not-verified") {
        setEmailNotVerified(true);
      } else if (err.code === "account-banned") {
        setError("Your account has been removed. Contact support@campusswap.in if this is a mistake.");
      } else if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(`Failed to sign in. (${err.code || err.message || "unknown error"})`);
      }
    } finally { setLoading(false); }
  }

  async function handleResendVerification() {
    setResendLoading(true);
    try {
      await resendVerificationEmail();
      setResendDone(true);
    } catch (e) {}
    setResendLoading(false);
  }

  async function handleGoogleLogin() {
    try {
      setError(""); setLoading(true);
      await loginWithGoogle();
      navigate("/");
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") return;
      if (err.code === "account-banned") setError("Your account has been removed. Contact support@campusswap.in if this is a mistake.");
      else setError("Google sign-in failed. Please try again.");
    } finally { setLoading(false); }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setForgotError(""); setForgotLoading(true);
    try {
      await sendPasswordResetEmail(auth, forgotEmail, {
        url: "https://campusswap.in/login",
        handleCodeInApp: false,
      });
      setForgotSent(true);
    } catch (err) {
      if (err.code === "auth/user-not-found") setForgotError("No account found with this email.");
      else if (err.code === "auth/invalid-email") setForgotError("Invalid email address.");
      else if (err.code === "auth/too-many-requests") setForgotError("Too many attempts. Try again later.");
      else setForgotError("Failed to send reset email. Try again.");
    }
    setForgotLoading(false);
  }

  async function sendOTP(e) {
    e.preventDefault();
    setPhoneError("");
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) { setPhoneError("Enter a valid 10-digit mobile number."); return; }
    setPhoneLoading(true);
    try {
      // Set up reCAPTCHA if not ready
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      }
      const confirmation = await signInWithPhoneNumber(auth, `+91${digits}`, recaptchaRef.current);
      confirmationRef.current = confirmation;
      setOtpSent(true);
    } catch (err) {
      if (err.code === "auth/too-many-requests") setPhoneError("Too many OTP requests. Try again later.");
      else if (err.code === "auth/invalid-phone-number") setPhoneError("Invalid phone number.");
      else setPhoneError("Could not send OTP. Check the number and try again.");
      // Reset reCAPTCHA on error
      try { recaptchaRef.current?.clear(); } catch (e) {}
      recaptchaRef.current = null;
    }
    setPhoneLoading(false);
  }

  async function verifyOTP(e) {
    e.preventDefault();
    if (otp.length !== 6) { setPhoneError("Enter the 6-digit OTP."); return; }
    setPhoneLoading(true);
    setPhoneError("");
    try {
      const result = await confirmationRef.current.confirm(otp);
      const uid = result.user.uid;
      const snap = await get(ref(db, `users/${uid}`));
      if (snap.exists() && snap.val()?.name) {
        navigate("/");
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") setPhoneError("Wrong OTP. Please try again.");
      else if (err.code === "auth/code-expired") setPhoneError("OTP expired. Please request a new one.");
      else setPhoneError("Verification failed. Try again.");
    }
    setPhoneLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex flex-col justify-center px-6 py-12">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-white tracking-tight">
          Campus<span className="text-yellow-300">Swap</span>
        </h1>
        <p className="text-blue-200 mt-2 text-sm">India's verified campus marketplace</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-auto w-full">

        {/* ── Email not verified screen ── */}
        {emailNotVerified ? (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
              <Mail size={28} className="text-yellow-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">Verify your email</h2>
            <p className="text-sm text-gray-500">
              We sent a verification link to <strong>{email}</strong>.<br />
              Click the link in the email to activate your account.
            </p>
            <p className="text-xs text-orange-500 font-medium">Not in inbox? Check your Spam / Junk folder.</p>
            {resendDone ? (
              <p className="text-sm text-green-600 font-semibold">✅ Verification email resent!</p>
            ) : (
              <button onClick={handleResendVerification} disabled={resendLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                {resendLoading ? "Sending..." : "Resend Verification Email"}
              </button>
            )}
            <button onClick={() => { setEmailNotVerified(false); setResendDone(false); }}
              className="w-full text-gray-500 text-sm hover:text-gray-700">← Back to login</button>
          </div>
        ) : phoneMode ? (
          <>
            <button onClick={() => { setPhoneMode(false); setOtpSent(false); setPhone(""); setOtp(""); setPhoneError(""); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 mb-4 hover:text-gray-700">
              <ArrowLeft size={15} /> Back
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {otpSent ? "Enter OTP" : "Login with Phone"}
            </h2>
            <p className="text-gray-500 text-sm mb-5">
              {otpSent ? `OTP sent to +91 ${phone}` : "We'll send a 6-digit OTP to your number"}
            </p>

            {phoneError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />{phoneError}
              </div>
            )}

            {!otpSent ? (
              <form onSubmit={sendOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <div className="relative flex items-center border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                    <span className="pl-3 pr-2 text-sm font-medium text-gray-500 bg-gray-50 border-r border-gray-200 py-3 flex-shrink-0">+91</span>
                    <input
                      type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="9876543210" maxLength={10}
                      className="flex-1 px-3 py-3 text-sm outline-none bg-white"
                    />
                  </div>
                </div>
                {/* Invisible reCAPTCHA mounts here */}
                <div id="recaptcha-container" />
                <button type="submit" disabled={phoneLoading}
                  className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-60">
                  {phoneLoading ? "Sending OTP..." : "Send OTP"}
                </button>
              </form>
            ) : (
              <form onSubmit={verifyOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">6-digit OTP</label>
                  <input
                    type="number" value={otp} onChange={e => setOtp(e.target.value.slice(0, 6))}
                    placeholder="Enter OTP" maxLength={6}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 text-center text-xl font-bold tracking-[0.5em]"
                    autoFocus
                  />
                </div>
                <button type="submit" disabled={phoneLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                  {phoneLoading ? "Verifying..." : "Verify & Login"}
                </button>
                <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setPhoneError(""); recaptchaRef.current = null; }}
                  className="w-full text-gray-500 text-sm hover:text-gray-700">
                  Resend OTP
                </button>
              </form>
            )}
          </>

        ) : !forgotMode ? (
          /* ── Email/Password mode (unchanged) ── */
          <>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Welcome back</h2>
            <p className="text-gray-500 text-sm mb-5">Sign in to your account</p>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />{error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com" required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <button type="button" onClick={() => { setForgotMode(true); setForgotEmail(email); setForgotSent(false); setForgotError(""); }}
                    className="text-xs text-blue-600 hover:underline">Forgot password?</button>
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password" required
                    className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Google Sign-In */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50 transition shadow-sm disabled:opacity-60 mb-3"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>

            {/* Phone OTP button */}
            <button
              onClick={() => { setPhoneMode(true); setError(""); }}
              className="w-full flex items-center justify-center gap-2 border-2 border-green-500 text-green-600 py-3 rounded-xl font-semibold text-sm hover:bg-green-50 transition"
            >
              <Phone size={16} /> Login with Phone OTP
            </button>
          </>

        ) : (
          /* ── Forgot password mode (unchanged) ── */
          <>
            {!forgotSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Reset Password</h2>
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-700">
                  Enter your email and we'll send a reset link.
                </div>
                {forgotError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                    <AlertCircle size={16} className="flex-shrink-0" />{forgotError}
                  </div>
                )}
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    placeholder="your@email.com" required autoFocus
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <button type="submit" disabled={forgotLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>
                <button type="button" onClick={() => setForgotMode(false)}
                  className="w-full text-gray-500 text-sm hover:text-gray-700">← Back to login</button>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail size={28} className="text-green-600" />
                </div>
                <p className="font-semibold text-gray-800">Reset link sent!</p>
                <p className="text-sm text-gray-500">Check <strong>{forgotEmail}</strong> for the reset link.</p>
                <p className="text-xs text-orange-500 mt-1 font-medium">Not in inbox? Check your Spam / Junk folder.</p>
                <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700">
                  Back to Login
                </button>
              </div>
            )}
          </>
        )}

        <p className="text-center text-sm text-gray-500 mt-5">
          Don't have an account?{" "}
          <Link to="/signup" className="text-blue-600 font-semibold hover:underline">Sign Up</Link>
        </p>
      </div>

      <p className="text-center text-blue-300 text-xs mt-6">
        By signing in, you agree to our Terms &amp; Privacy Policy
      </p>
    </div>
  );
}
